import React from 'react';

import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleCardTemplate from '@/components/layouts/SingleCardTemplate';
import Loading from '@/components/ui/loading/Loading';
import { pageTitles } from '@/constants/pageTitles';
import useValidateInvitation from '@/hooks/invitations/useValidateInvitation';

import {
  PARENTS_LIMIT_EXCEEDED_ERROR_CODE,
  PARENTS_LIMIT_EXCEEDED_FOR_EXISTING_PARENT_ERROR_CODE,
} from '../constants';
import { getInvitationToken } from '../invitationToken';

import InvitationStudentConfirmForm from './InvitationStudentConfirmForm';
import InvitationValidateErrorPage from './InvitationValidateErrorPage';

const InvitationStudentConfirmContent: React.FC<{
  invitationToken: string;
}> = ({ invitationToken }) => {
  const { data, isFetching, error } = useValidateInvitation({
    invitationToken,
  });
  const student = data?.student;
  const isExistingParent = data?.is_existing_parent ?? false;

  if (isFetching) {
    return (
      <SingleCardTemplate title={pageTitles.invitationStudentConfirm}>
        <Loading />
      </SingleCardTemplate>
    );
  }

  if (isExistingParent) {
    return <Navigate to="/invitations/accept" replace />;
  }

  if (error) {
    if (
      error.error_code === PARENTS_LIMIT_EXCEEDED_FOR_EXISTING_PARENT_ERROR_CODE
    ) {
      return <Navigate to="/invitations/accept" replace />;
    }
    if (error.error_code === PARENTS_LIMIT_EXCEEDED_ERROR_CODE) {
      return (
        <InvitationValidateErrorPage
          type="parents_limit"
          pageTitle={pageTitles.invitationStudentConfirm}
        />
      );
    }
    if (error.status === 403) {
      return (
        <InvitationValidateErrorPage
          type="expired"
          pageTitle={pageTitles.invitationStudentConfirm}
        />
      );
    }
    if (error.status === 400 || error.status === 422) {
      return (
        <InvitationValidateErrorPage
          type="invalid"
          pageTitle={pageTitles.invitationStudentConfirm}
        />
      );
    }
    if (error.status >= 500) {
      return <ErrorPage type="internal_server_error" />;
    }
    return <ErrorPage />;
  }

  if (!student) {
    return <ErrorPage type="not_found" />;
  }

  return (
    <SingleCardTemplate title={pageTitles.invitationStudentConfirm}>
      <Helmet>
        <title>{pageTitles.invitationStudentConfirm}</title>
      </Helmet>
      <InvitationStudentConfirmForm student={student} />
    </SingleCardTemplate>
  );
};

const InvitationStudentConfirmPage: React.FC = () => {
  const invitationToken = getInvitationToken();

  if (!invitationToken) {
    return (
      <InvitationValidateErrorPage
        type="token_missing"
        pageTitle={pageTitles.invitationStudentConfirm}
      />
    );
  }

  return <InvitationStudentConfirmContent invitationToken={invitationToken} />;
};

export default InvitationStudentConfirmPage;
